import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Maximize2 } from "lucide-react";
import { KeyValuePairType } from "@repo/types/enums";
import { UpdateTicketField } from "../../types/enums";
import type { KeyValuePairDTO } from "@repo/types/dtos";
import type { TicketDTO } from "../../types/dtos";
import {
  KeyValueViewType,
  type KeyValueItem,
} from "@repo/types/ui/keyValuePairs";

import type { RecordId, RecordProps } from "@repo/types/ui/tables";
import { useFormatDate } from "../useFormatDate";
import {
  useAddUpdateTicket,
  useGetLinkedAccounts,
  useGetKeyValuePairs,
} from "../queries";
import { useUserKeyValueItems } from "@repo/hooks/queries";
import {
  getKeyValueMenuButtonRenderer,
  getUserMenuButtonRenderer,
} from "../../components/tickets/helpers/TicketFields";
import { TicketColumns } from "../../helpers/TicketColumns";
import { buildColumn } from "../helpers/columnBuilder";
import TruncatedText from "../../components/ui/TruncatedText";
import KeyValueMenu from "../../components/ui/menu/KeyValueMenu";

const useTicketColumns = ({
  onOpenRecord: onOpenTicket,
  //onUpdateRecord: onUpdateTicket,
}: RecordProps): ColumnDef<TicketDTO>[] => {
  const { formatDate } = useFormatDate();

  const getKeyValueItems = (
    keyValuePairs: KeyValuePairDTO[],
  ): KeyValueItem[] => {
    return keyValuePairs.map(
      (keyValuePair: KeyValuePairDTO): KeyValueItem => ({
        viewType: KeyValueViewType.textOnly,
        Id: keyValuePair.Id,
        Value: keyValuePair.Value,
        Description: keyValuePair.Description ?? "",
        PairType: keyValuePair.PairType ?? 0,
        PairTypeEnumValue: keyValuePair.PairTypeEnumValue ?? 0,
        BgColorHex: keyValuePair.BgColorHex,
        TextColorHex: keyValuePair.TextColorHex,
        SortOrder: keyValuePair.SortOrder ?? 0,
        classNames: "p-2 mb-2 items-center justify-center hover:brightness-90",
      }),
    );
  };

  const { data: ticketStatuses } = useGetKeyValuePairs(
    KeyValuePairType.TicketStatus,
    getKeyValueItems,
  );

  const { data: ticketPriorities } = useGetKeyValuePairs(
    KeyValuePairType.TicketPriority,
    getKeyValueItems,
  );

  const { mutate: updateTicket } = useAddUpdateTicket();
  const { data: userKeyValues } = useUserKeyValueItems();
  const { data: linkedAccounts } = useGetLinkedAccounts((la) =>
    la.map(
      (account): KeyValueItem => ({
        viewType: KeyValueViewType.textOnly,
        Id: account.Id,
        Value: account.Email,
        BgColorHex: account.BgColorHex,
        TextColorHex: account.TextColorHex,
        classNames: "p-2 mb-2 items-center justify-center hover:brightness-90",
      }),
    ),
  );

  const columns = useMemo<ColumnDef<TicketDTO>[]>(
    () => [
      buildColumn<TicketDTO>(TicketColumns.title, {
        size: 350,
        enablePinning: true,
        meta: { align: "left", padding: "sm" },
        cell: ({ row }) => (
          <div
            className="group cursor-pointer flex gap-x-5"
            onClick={() => onOpenTicket(row.original.TicketGuid)}
          >
            <TruncatedText text={row.original.Title} maxLength={40} />

            <Maximize2
              className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer"
              size={15}
            />
          </div>
        ),
      }),
      buildColumn<TicketDTO>(TicketColumns.channel, {
        size: 150,
        enablePinning: false,
        cell: ({ row }) => (
          <KeyValueMenu
            selectedKey={row.original.EntityId}
            selectedKeyClassName="p-1 rounded"
            items={linkedAccounts}
            onValueChange={async (newValue) => {
              updateTicket({
                Id: row.original.Id,
                EntityId: newValue,
                UpdateField: UpdateTicketField.EntityId,
              });
            }}
            triggerRenderer={(selected) =>
              getKeyValueMenuButtonRenderer(
                linkedAccounts ?? [],
                selected ?? row.original.EntityId,
              )
            }
          />
        ),
      }),
      buildColumn<TicketDTO>(TicketColumns.assignee, {
        size: 100,
        enablePinning: false,
        cell: ({ row }) => (
          <KeyValueMenu
            selectedKey={row.original.AssignedToId}
            items={userKeyValues}
            onValueChange={async (newValue) => {
              updateTicket({
                Id: row.original.Id,
                AssignedToId: newValue,
                UpdateField: UpdateTicketField.AssignedToId,
              });
            }}
            triggerRenderer={(selectedItem) =>
              getUserMenuButtonRenderer(
                userKeyValues ?? [],
                selectedItem ?? row.original.AssignedToId,
              )
            }
            maxHeight={300}
            mode="assignee"
            isMulti={true}
          />
        ),
      }),
      buildColumn<TicketDTO>(TicketColumns.status, {
        size: 150,
        enablePinning: false,
        enableSorting: false,
        cell: ({ row }) => (
          <KeyValueMenu
            selectedKey={row.original.StatusKeyValuePairId}
            items={ticketStatuses}
            enumType={KeyValuePairType.TicketStatus}
            onValueChange={async (newValue: RecordId) => {
              updateTicket({
                Id: row.original.Id,
                StatusKeyValuePairId: newValue,
                UpdateField: UpdateTicketField.Status,
              });
            }}
            triggerRenderer={(selected: RecordId) =>
              getKeyValueMenuButtonRenderer(
                ticketStatuses ?? [],
                selected ?? row.original.StatusKeyValuePairId,
              )
            }
            itemsEditable={true}
            editText={"Edit Statuses"}
            popupClassNames="p-2 w-55"
            maxHeight={300}
          />
        ),
      }),
      buildColumn<TicketDTO>(TicketColumns.priority, {
        size: 150,
        enablePinning: false,
        cell: ({ row }) => (
          <KeyValueMenu
            selectedKey={row.original.PriorityKeyValuePairId}
            items={ticketPriorities}
            enumType={KeyValuePairType.TicketPriority}
            onValueChange={async (newValue: RecordId) => {
              updateTicket({
                Id: row.original.Id,
                PriorityKeyValuePairId: newValue,
                UpdateField: UpdateTicketField.Priority,
              });
            }}
            triggerRenderer={(selected: RecordId) =>
              getKeyValueMenuButtonRenderer(
                ticketPriorities ?? [],
                selected ?? row.original.PriorityKeyValuePairId,
              )
            }
            itemsEditable={true}
            editText={"Edit Priorities"}
            popupClassNames="p-2 w-55"
            maxHeight={300}
          />
        ),
      }),
      buildColumn<TicketDTO>(TicketColumns.description, {
        size: 350,
        enablePinning: false,
        meta: { align: "left", padding: "sm" },
        cell: ({ row }) => (
          <TruncatedText text={row.original.Description} maxLength={50} />
        ),
      }),
      buildColumn<TicketDTO>(TicketColumns.attachments, {
        size: 150,
        enablePinning: false,
        enableSorting: false,
        // cell: ({ row }) => (
        //   <FileAttachments
        //     entityId={row.original.Id}
        //     entityType={EntityType.Ticket}
        //     showAll={false}
        //   />
        // ),
      }),
      buildColumn<TicketDTO>(TicketColumns.updatedOn, {
        size: 200,
        enablePinning: false,
        cell: ({ row }) => {
          return row.original.UpdatedOn
            ? formatDate(row.original.UpdatedOn, "long")
            : "";
        },
      }),
      buildColumn<TicketDTO>(TicketColumns.createdOn, {
        size: 200,
        enablePinning: false,
        cell: ({ row }) => {
          return row.original.CreatedOn
            ? formatDate(row.original.CreatedOn, "long")
            : "";
        },
      }),
      buildColumn<TicketDTO>(TicketColumns.id, {
        size: 100,
        enablePinning: false,
      }),
    ],
    [
      onOpenTicket,
      ticketPriorities,
      ticketStatuses,
      updateTicket,
      userKeyValues,
      linkedAccounts,
      formatDate,
    ],
  );

  return columns;
};

export { useTicketColumns };
